import { ApiError } from "../composables/useApi";

// The API reports a missing or foreign-org resource with this stable 404
// envelope message, and apiFetch surfaces envelope messages verbatim; the
// second form is apiFetch's fallback when a 404 arrives without an envelope.
const NOT_FOUND_MESSAGES = new Set([
  "The resource does not exist in your organization",
  "Request failed (404)",
]);

/** True when an `apiFetch` failure represents a missing (404) resource. */
export function isNotFoundError(err: unknown): boolean {
  return err instanceof Error && NOT_FOUND_MESSAGES.has(err.message);
}

/**
 * True when an `apiFetch` failure is the backend refusing billable work
 * because the organization's effective spending limit is exhausted (402,
 * code `credit_exhausted`) — the caller can point the user at the billing
 * page to add a payment method, raise the allowance, or settle an invoice.
 */
export function isCreditExhausted(err: unknown): err is ApiError {
  return err instanceof ApiError && (err.code === "credit_exhausted" || err.status === 402);
}

/**
 * True when an `apiFetch` failure is a 501 Not Implemented — an operation the
 * contract declares but whose server side is not deployed yet. A surface that
 * expects the capability to arrive treats this like a missing resource (an
 * honest empty/disabled state), not a hard error.
 */
export function isNotImplemented(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 501;
}
